import React, { useState } from 'react';
import { FiInfo } from 'react-icons/fi';

const MetricInfoButton = ({ metricType, className = "" }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  // Define metric information based on type
  const getMetricInfo = (type) => {
    const metricInfo = {
      'performance': {
        title: 'Performance Metrics',
        description: 'Core execution metrics including duration, tokens, cost, and latency.',
        details: [
          'Duration: Total execution time of the run',
          'Tokens: Number of tokens processed by the LLM',
          'Cost: Estimated cost of the API calls',
          'Latency: Response time for each LLM call'
        ]
      },
      'llm-calls': {
        title: 'LLM Call Chain',
        description: 'Detailed breakdown of each LLM API call in the execution chain.',
        details: [
          'Model: The AI model used for each call',
          'Input/Output Tokens: Token usage breakdown',
          'Cost: Individual call cost calculation',
          'Latency: Response time per call'
        ]
      },
      'guardrails': {
        title: 'Guardrails Metrics',
        description: 'AI safety and compliance metrics to ensure responsible AI usage.',
        details: [
          'Content Safety: Toxicity, bias, and safety scores',
          'Prompt Injection: Detection of malicious prompts',
          'PII Detection: Personal information identification',
          'Compliance: Regulatory compliance status'
        ]
      },
      'critical': {
        title: 'Critical Metrics Analysis',
        description: 'Comprehensive analysis of AI system performance across embedding, RAG, and truthfulness dimensions.',
        details: [
          'Embedding Metrics: Vector generation and retrieval performance (speed, accuracy, confidence)',
          'RAG Metrics: Document retrieval and response generation quality (faithfulness, relevance, hit rate)',
          'Truthfulness Metrics: Factual accuracy and reliability assessment (faithfulness scores, hallucination detection)'
        ]
      },
      'ocr': {
        title: 'OCR Metrics',
        description: 'Optical Character Recognition performance and accuracy metrics.',
        details: [
          'Text Length: Number of characters processed',
          'Confidence Score: OCR accuracy percentage',
          'Duration: Processing time for OCR',
          'Model: OCR model used for processing'
        ]
      },
             'logs': {
         title: 'Errors & Logs',
         description: 'System logs and error messages for debugging and monitoring.',
         details: [
           'Error Messages: Detailed error information',
           'Log Levels: Info, warning, and error logs',
           'Timestamps: When each log entry occurred',
           'Source: Component that generated the log'
         ]
       },
       'overall-system': {
         title: 'Overall System Score',
         description: 'Comprehensive system performance and health assessment metrics.',
         details: [
           'System Health: Overall system performance score',
           'Resource Utilization: CPU, memory, and network usage',
           'Response Quality: AI model response accuracy and relevance',
           'Cost Efficiency: Cost per request and optimization metrics',
           'Reliability: System uptime and error rate indicators'
         ]
       },
       'prompt-response': {
         title: 'Prompt and Response',
         description: 'Actual prompts sent to the AI model and the corresponding responses received.',
         details: [
           'Input Prompts: The original text sent to the AI model',
           'Model Responses: The AI-generated responses and outputs',
           'Context: Additional context or instructions provided',
           'Processing Details: How the prompt was processed and formatted'
         ]
       },
       'system-metrics': {
         title: 'System Metrics',
         description: 'System-level performance and resource utilization metrics.',
         details: [
           'CPU Usage: Processor utilization and performance',
           'Memory Usage: RAM consumption and efficiency',
           'Network I/O: Network traffic and bandwidth usage',
           'Process Status: Current system process information',
           'Collection Timestamps: When metrics were gathered'
         ]
       },
       'custom-metrics': {
         title: 'Custom Metrics',
         description: 'User-defined and application-specific metrics for monitoring.',
         details: [
           'Execution Duration: Total time taken for the run',
           'Total LLM Calls: Number of API calls made',
           'Total Tokens: Combined input and output tokens',
           'Total Cost: Estimated cost of all operations',
           'Custom Logs: Application-specific metrics and data'
         ]
       },
       'projects-overview': {
         title: 'Projects Overview',
         description: 'Comprehensive view of all AI projects and their performance metrics.',
         details: [
           'Project Management: View and manage all AI projects',
           'Performance Tracking: Monitor project execution metrics',
           'Resource Allocation: Track resource usage per project',
           'Cost Analysis: Analyze spending patterns across projects',
           'Status Monitoring: Real-time project status and health'
         ]
       },
       'cost-analytics': {
         title: 'Cost Analytics',
         description: 'Detailed analysis of API usage costs and spending patterns.',
         details: [
           'Cost Tracking: Monitor API usage expenses',
           'Spending Patterns: Analyze cost trends over time',
           'Budget Management: Track against usage limits',
           'Cost Optimization: Identify cost-saving opportunities',
           'Usage Forecasting: Predict future spending'
         ]
       },
       'query-analytics': {
         title: 'Query Analytics',
         description: 'Analysis of prompts and responses for optimization insights.',
         details: [
           'Prompt Analysis: Evaluate prompt effectiveness',
           'Response Quality: Assess AI response accuracy',
           'Performance Metrics: Track query performance',
           'Optimization Insights: Identify improvement opportunities',
           'Usage Patterns: Understand query patterns and trends'
         ]
       },
       'log-analytics': {
         title: 'Log Analytics',
         description: 'System logs monitoring and troubleshooting tools.',
         details: [
           'Error Tracking: Monitor and analyze system errors',
           'Performance Logs: Track system performance metrics',
           'Debugging Tools: Troubleshoot issues efficiently',
           'System Health: Monitor overall system status',
           'Audit Trail: Maintain comprehensive activity logs'
         ]
       },
       'rag-metrics': {
         title: 'RAG Metrics',
         description: 'Retrieval-Augmented Generation performance and accuracy metrics for AI systems that combine document retrieval with text generation.',
         details: [
           'Answer Faithfulness: Measures how accurately responses reflect the retrieved source documents (0-100%)',
           'Answer Relevance: Evaluates how well responses address the user query (0-100%)',
           'Hit Rate: Success rate of finding relevant documents for queries (0-100%)',
           'Format Correctness: Ensures responses follow proper structure and formatting requirements',
           'Queries/Second: Processing speed of retrieval operations',
           'Error Rate: Percentage of failed retrieval or generation attempts',
           'Total Queries: Number of queries processed in this run'
         ]
       },
       'embedding-metrics': {
         title: 'Embedding Metrics',
         description: 'AI embedding generation and retrieval performance metrics for converting text to numerical vectors.',
         details: [
           'Retrieval Duration: Time taken to find and retrieve relevant embeddings from vector database (milliseconds)',
           'Relevance Score: Accuracy of semantic similarity matching between query and retrieved embeddings (0-100%)',
           'Generation Time: Time required to convert text into embedding vectors (milliseconds)',
           'Confidence Score: Reliability and quality assessment of the embedding generation process (0-100%)',
           'Vector Operations: Efficiency of mathematical operations on embedding vectors',
           'Similarity Matching: Quality of finding semantically similar content'
                  ]
       },
       'truthfulness-metrics': {
         title: 'Truthfulness Metrics',
         description: 'Comprehensive assessment of AI response accuracy, factual correctness, and reliability.',
         details: [
           'High Faithfulness: Responses with excellent factual accuracy (>80% score)',
           'Moderate Faithfulness: Responses with good but improvable accuracy (60-80% score)',
           'Average Faithfulness: Overall truthfulness score across all responses (0-100%)',
           'Factual Accuracy: Cross-referencing responses with reliable source documents',
           'Claim Verification: Verification of claims made in responses',
           'Source Attribution: Tracking which sources support each claim in responses',
           'Contradiction Detection: Identifying false, fabricated, or unsupported information',
           'Overall Truthfulness: Comprehensive truthfulness assessment score'
         ]
       },
       'content-safety': {
         title: 'Content Safety',
         description: 'AI content safety and compliance monitoring metrics.',
         details: [
           'Toxicity Detection: Identify harmful or inappropriate content',
           'Bias Assessment: Detect and measure content bias',
           'Safety Scoring: Overall content safety evaluation',
           'Compliance Monitoring: Regulatory and policy adherence',
           'Risk Assessment: Potential safety concerns and alerts'
         ]
       },
       'custom-guardrails': {
         title: 'Custom Guardrails',
         description: 'User-defined safety and compliance rules and metrics.',
         details: [
           'Custom Rules: User-defined safety and compliance policies',
           'Rule Enforcement: Monitoring of custom rule violations',
           'Policy Compliance: Adherence to organizational policies',
           'Custom Alerts: User-defined warning and error thresholds',
           'Flexible Monitoring: Adaptable safety and compliance checks'
         ]
       },
       'prompt-injection-detection': {
         title: 'Prompt Injection Detection',
         description: 'Detection and prevention of malicious prompt injection attacks.',
         details: [
           'Injection Detection: Identify malicious prompt manipulation',
           'Attack Prevention: Block harmful injection attempts',
           'Confidence Scoring: Reliability of detection accuracy',
           'Pattern Recognition: Identify suspicious prompt patterns',
           'Security Monitoring: Continuous threat detection and response'
         ]
       },
       'response-quality': {
         title: 'Response Quality',
         description: 'Assessment of AI response accuracy, relevance, and usefulness.',
         details: [
           'Accuracy Metrics: Factual correctness of responses',
           'Relevance Scoring: How well responses match user intent',
           'Completeness: Thoroughness of response coverage',
           'Clarity Assessment: Readability and understandability',
           'Usefulness Rating: Practical value of responses to users'
         ]
       }
    };

    return metricInfo[type] || {
      title: 'Metric Information',
      description: 'Information about this metric category.',
      details: ['No specific information available for this metric type.']
    };
  };

  const info = getMetricInfo(metricType);

  return (
    <div className={`relative inline-block ${className}`}>
      <button
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onClick={() => setShowTooltip(!showTooltip)}
        className="inline-flex items-center justify-center w-5 h-5 text-gray-400 hover:text-blue-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-full"
        title={`Learn more about ${info.title}`}
      >
        <FiInfo className="w-3 h-3" />
      </button>

      {/* Tooltip */}
      {showTooltip && (
        <div className="absolute z-50 w-80 p-4 bg-white border border-gray-200 rounded-lg shadow-lg -top-2 left-6 transform -translate-y-full">
          {/* Arrow */}
          <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-200"></div>
          
          <div className="space-y-3">
            <div>
              <h4 className="text-sm font-semibold text-gray-900 font-['Manrope']">{info.title}</h4>
              <p className="text-xs text-gray-600 mt-1 font-['Manrope']">{info.description}</p>
            </div>
            
            <div>
              <h5 className="text-xs font-medium text-gray-700 mb-2 font-['Manrope']">Key Details:</h5>
              <ul className="space-y-1">
                {info.details.map((detail, index) => (
                  <li key={index} className="text-xs text-gray-600 font-['Manrope'] flex items-start">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                    {detail}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetricInfoButton; 